import { useCallback, useEffect, useMemo, useRef } from "react";
import { Snackbar, Alert } from "@mui/material";
import Button from "../Button/Button";

const AppSnackbar = ({
  open = false,
  type = "info", // success | error | warning | info
  message = "",
  autoHideDuration = 2000,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  showAction = false,
  actionText = "OK",
  onClose,
  onAction,
  sx = {},
}) => {
  const baseZIndex = 11000; // above mobile burger and modal overlay
  const baseStyle = {
    zIndex: baseZIndex,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    pointerEvents: "none", // allow clicks through container; alert keeps pointer events
    "& .MuiAlert-root": {
      pointerEvents: "auto",
      margin: "0 auto",
      width: "fit-content",
      maxWidth: "calc(100vw - 32px)",
    },
  };

  const computedSx = useMemo(() => {
    if (typeof sx === "function") {
      return (theme) => {
        const resolved = sx(theme) || {};
        const providedZ = typeof resolved.zIndex === "number" ? resolved.zIndex : baseZIndex;
        return {
          ...baseStyle,
          ...resolved,
          zIndex: Math.max(baseZIndex, providedZ),
        };
      };
    }
    const providedZ = typeof sx?.zIndex === "number" ? sx.zIndex : baseZIndex;
    return {
      ...baseStyle,
      ...(sx || {}),
      zIndex: Math.max(baseZIndex, providedZ),
    };
  }, [sx, baseZIndex, baseStyle]);

  const adjustedAutoHideDuration = useMemo(() => {
    if ((type === "error" || type === "warning") && autoHideDuration <= 2000) {
      return 4000;
    }
    return autoHideDuration;
  }, [autoHideDuration, type]);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingRef = useRef(adjustedAutoHideDuration);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      clearTimer();
      onClose?.(event, reason);
    },
    [clearTimer, onClose]
  );

  const startTimer = useCallback(
    (duration) => {
      if (!open || duration == null) return;
      clearTimer();
      remainingRef.current = duration;
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        handleClose(null, "timeout");
      }, duration);
    },
    [clearTimer, handleClose, open]
  );

  useEffect(() => {
    if (open && adjustedAutoHideDuration != null) {
      startTimer(adjustedAutoHideDuration);
    } else {
      clearTimer();
    }

    return clearTimer;
  }, [adjustedAutoHideDuration, clearTimer, open, startTimer]);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current || startTimeRef.current == null) return;
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
  }, [clearTimer]);

  const resumeTimer = useCallback(() => {
    if (!open || adjustedAutoHideDuration == null) return;
    if (remainingRef.current <= 0) {
      handleClose(null, "timeout");
      return;
    }
    startTimer(remainingRef.current);
  }, [adjustedAutoHideDuration, handleClose, open, startTimer]);

  const interactionHandlers = {
    onMouseEnter: pauseTimer,
    onMouseLeave: resumeTimer,
    onFocus: pauseTimer,
    onBlur: resumeTimer,
    onTouchStart: pauseTimer,
    onTouchEnd: resumeTimer,
    onPointerEnter: pauseTimer,
    onPointerLeave: resumeTimer,
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={null}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={computedSx}
      {...interactionHandlers}
    >
      <Alert
        onClose={handleClose}
        severity={type}
        variant="filled"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "auto",
          maxWidth: "min(520px, calc(100vw - 32px))",
          minWidth: 0,
          textAlign: "center",
          py: 0.75,
          px: 1.5,
        }}
        action={
          showAction ? (
            <Button
              variant="primary"
              size="small"
              sx={{ minWidth: 70, textTransform: "none" }}
              // onClick={() => {
              //   onAction?.();
              //   onClose?.();
              // }}
              onClick={(event) => {
                onAction?.();
                handleClose(event, "action");
              }}
            >
              {actionText}
            </Button>
          ) : null
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default AppSnackbar;
